import * as React from "react";
import { ExpireClientSession } from "source/app/services/Authentication/ClientSession";
import * as toastr from "toastr";
import { SignOut } from "../../infrastructure/Authorizer/SignOut";
import { ReloadApp } from "../App";
import { StyleClass } from "./Styles/CSS";
import { SendClientErrorReportAsync } from "./ErrorBoundary/SendClientErrorReport";
import {
    AutoErrorReportStatus,
    StartAutomaticClientErrorReport,
} from "./ErrorBoundary/AutoClientErrorReport";
import {
    GetAutomaticReportStatusText,
    GetErrorDiagnostics,
    GetErrorSummary,
    GetReportButtonText,
    IsAuthorizationError,
} from "./ErrorBoundary/ErrorBoundaryHelpers";

interface IErrorBoundaryState {
    error: any;
    errorInfo: any;
    isAuthorizationError: boolean;
    reportStatus: "idle" | "sending" | "sent" | "failed";
    incidentId: string | null;
    autoReportStatus: AutoErrorReportStatus;
}

let showClientError: ((error: any) => void) | undefined;
export function ShowClientError(error: any) {
    if (IsAuthorizationError(error)) return ExpireClientSession();
    showClientError?.(error);
}

class ErrorBoundary extends React.Component<{}, IErrorBoundaryState> {
    constructor(props: {}) {
        super(props);
        this.state = {
            error: null,
            errorInfo: null,
            isAuthorizationError: false,
            reportStatus: "idle",
            incidentId: null,
            autoReportStatus: "idle",
        };
    }

    private isMounted = false;
    private hasFatalError = false;
    componentDidCatch(error: any, errorInfo: any) {
        this.SetFatalError(error, errorInfo);
    }
    componentDidMount() {
        this.isMounted = true;
        showClientError = (error) =>
            this.SetFatalError(error, {
                componentStack:
                    "The client received an error outside React rendering.",
            });
    }

    componentWillUnmount() {
        this.isMounted = false;
        showClientError = undefined;
    }

    render() {
        const sectionStyleClass = StyleClass(`ErrorBoundarySection`, {
            margin: `20px`,
        });

        if (this.state.errorInfo) {
            const signOutSection = (
                <>
                    {!this.state.isAuthorizationError && (
                        <div className={sectionStyleClass}>
                            If reloading doesn't work, try signing out:
                        </div>
                    )}
                    <div className={sectionStyleClass}>
                        <button
                            onClick={() => SignOut()}
                            style={{ color: "black", fontSize: `48px` }}
                        >
                            {this.state.isAuthorizationError
                                ? "Sign out (recommended)"
                                : "Sign out"}
                        </button>
                    </div>
                </>
            );

            return (
                <div>
                    <div className={sectionStyleClass}>
                        <h2>Something went wrong.</h2>
                        <details open style={{ whiteSpace: "pre-wrap" }}>
                            <div style={{ fontWeight: "bold" }}>
                                {GetErrorSummary(this.state.error)}
                            </div>
                            <div
                                style={{
                                    fontSize: "0.72em",
                                    marginTop: "20px",
                                }}
                            >
                                {GetErrorDiagnostics(this.state.error)}
                                <br />
                                {this.state.errorInfo.componentStack}
                            </div>
                        </details>
                    </div>
                    {this.state.isAuthorizationError && signOutSection}
                    {this.state.autoReportStatus !== "idle" && (
                        <div className={sectionStyleClass}>
                            {GetAutomaticReportStatusText(
                                this.state.autoReportStatus
                            )}
                        </div>
                    )}
                    <div className={sectionStyleClass}>
                        <button
                            disabled={
                                this.state.reportStatus !== "idle" ||
                                this.state.autoReportStatus === "sending"
                            }
                            onClick={() => this.SendErrorReportAsync()}
                            style={{ color: "black", fontSize: `32px` }}
                        >
                            {GetReportButtonText(this.state.reportStatus)}
                        </button>
                        {this.state.incidentId && (
                            <div>Error reference: {this.state.incidentId}</div>
                        )}
                    </div>
                    <div className={sectionStyleClass}>
                        <button
                            onClick={() => {
                                toastr.info("Reloading...", "Please wait");
                                ReloadApp();
                            }}
                            style={{ color: "black", fontSize: `48px` }}
                        >
                            Reload
                        </button>
                    </div>
                    {!this.state.isAuthorizationError && signOutSection}
                </div>
            );
        }

        return this.props.children;
    }

    private async SendErrorReportAsync() {
        this.setState({ reportStatus: "sending" });

        try {
            const incidentId = await SendClientErrorReportAsync(
                this.state.error,
                this.state.errorInfo
            );
            this.setState({ reportStatus: "sent", incidentId });
        } catch (error) {
            this.setState({ reportStatus: "failed" });
        }
    }

    private SetFatalError(error: any, errorInfo: any) {
        if (this.hasFatalError) return;
        this.hasFatalError = true;
        this.setState(
            {
                error,
                errorInfo,
                isAuthorizationError: IsAuthorizationError(error),
                reportStatus: "idle",
                incidentId: null,
                autoReportStatus: "idle",
            },
            () =>
                StartAutomaticClientErrorReport(
                    error,
                    errorInfo,
                    (autoReportStatus, incidentId) => {
                        if (!this.isMounted || this.state.error !== error) {
                            return;
                        }

                        this.setState({
                            autoReportStatus,
                            incidentId: incidentId || this.state.incidentId,
                        });
                    }
                )
        );
    }
}

export default ErrorBoundary;
